import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, RefreshCw, BookOpen, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// -----------------------------------------------------------------------
// AdminHealth — two side-by-side cards:
//   Left  · LLM Credit Usage (today total + 7-day sparkline + kind breakdown)
//   Right · Devotional Cache Health (8 language pills: green if cached today)
// Both auto-refresh every 60s. "Force prewarm" button warms every language
// (uses LLM credits — use sparingly).
// -----------------------------------------------------------------------
export default function AdminHealth() {
  const [usage, setUsage] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prewarming, setPrewarming] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [u, h] = await Promise.all([
        api.get("/admin/llm-usage"),
        api.get("/admin/devotional/health"),
      ]);
      setUsage(u.data);
      setHealth(h.data);
    } catch (err) {
      // silent — this is a passive widget
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  const runPrewarm = async () => {
    if (!window.confirm("Force-generate today's devotional in ALL 8 languages? This will consume ~8 LLM calls.")) return;
    setPrewarming(true);
    try {
      const { data } = await api.post("/admin/devotional/prewarm");
      toast.success(`Prewarm complete · ${data.already} cached, ${data.generated} generated, ${data.failed} failed`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPrewarming(false);
    }
  };

  return (
    <section className="grid gap-6 md:grid-cols-2 mb-10" data-testid="admin-health">
      <LlmUsageCard usage={usage} busy={busy} onRefresh={load} />
      <DevotionalHealthCard
        health={health}
        busy={busy}
        prewarming={prewarming}
        onPrewarm={runPrewarm}
        onRefresh={load}
      />
    </section>
  );
}

// -----------------------------------------------------------------------
function LlmUsageCard({ usage, busy, onRefresh }) {
  const kinds = usage?.today_by_kind || {};
  const series = usage?.last_7_days || [];
  const max = Math.max(1, ...series.map((s) => s.count));

  const KIND_LABEL = {
    devotional: "Devotional",
    mood: "Mood Guide",
    sermon: "Sermon Studio",
    renewal: "Renewal Of Mind",
    other: "Other",
  };
  const KIND_COLOR = {
    devotional: "bg-amber-500",
    mood: "bg-emerald-500",
    sermon: "bg-sky-500",
    renewal: "bg-violet-500",
    other: "bg-white/40",
  };

  return (
    <div className="border border-gold-500/25 bg-ink-paper/40 p-6" data-testid="llm-usage-card">
      <div className="flex items-center gap-3 mb-1">
        <Zap size={18} className="text-gold-500" />
        <h3 className="font-serif text-xl text-white">LLM Credit Usage</h3>
        <button
          onClick={onRefresh}
          disabled={busy}
          aria-label="Refresh"
          data-testid="llm-usage-refresh"
          className="ml-auto text-white/50 hover:text-gold-400 disabled:opacity-40 transition-colors"
        >
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-white/50 text-xs mb-6">
        Every call to Devotional, Mood Guide, Sermon Studio or Renewal counts one credit.
      </p>

      <div className="flex items-baseline gap-3 mb-1">
        <span className="font-serif text-4xl text-gold-300" data-testid="llm-usage-today">
          {usage?.today_total ?? "—"}
        </span>
        <span className="text-[10px] uppercase tracking-[0.24em] text-white/50">
          calls today · {usage?.last_7_days_total ?? 0} last 7 days
        </span>
      </div>

      {/* 7-day sparkline */}
      <div className="mt-5 flex items-end gap-1.5 h-14" data-testid="llm-usage-sparkline">
        {series.map((s) => {
          const h = Math.round((s.count / max) * 100);
          const isToday = s.date === usage?.today;
          return (
            <div key={s.date} className="flex-1 flex flex-col items-center gap-1" title={`${s.date}: ${s.count} calls`}>
              <div
                className={`w-full transition-all ${isToday ? "bg-gold-500" : "bg-gold-500/30"}`}
                style={{ height: `${Math.max(4, h)}%` }}
              />
              <span className={`text-[9px] uppercase tracking-[0.14em] ${isToday ? "text-gold-400 font-semibold" : "text-white/40"}`}>
                {s.date.slice(5)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Kind breakdown */}
      <div className="mt-6 space-y-1.5" data-testid="llm-usage-kinds">
        {Object.keys(kinds).length === 0 ? (
          <p className="text-white/40 text-xs italic">No LLM calls made yet today.</p>
        ) : (
          Object.entries(kinds).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 ${KIND_COLOR[k] || KIND_COLOR.other}`} />
              <span className="text-white/70 flex-1">{KIND_LABEL[k] || k}</span>
              <span className="font-mono text-gold-300 font-semibold">{n}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
function DevotionalHealthCard({ health, busy, prewarming, onPrewarm, onRefresh }) {
  const languages = health?.languages || [];
  const cachedCount = health?.cached_count || 0;
  const total = health?.total || 8;
  const isFullyCached = cachedCount === total && total > 0;

  const LANG_FLAGS = {
    en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪", es: "🇪🇸",
    ru: "🇷🇺", sw: "🇰🇪", "zh-CN": "🇨🇳", ar: "🇸🇦",
  };
  const LANG_NAME = {
    en: "English", fr: "Français", de: "Deutsch", es: "Español",
    ru: "Русский", sw: "Kiswahili", "zh-CN": "中文", ar: "العربية",
  };

  return (
    <div className="border border-gold-500/25 bg-ink-paper/40 p-6" data-testid="devotional-health-card">
      <div className="flex items-center gap-3 mb-1">
        <BookOpen size={18} className={isFullyCached ? "text-emerald-400" : "text-amber-400"} />
        <h3 className="font-serif text-xl text-white">Devotional Health</h3>
        <button
          onClick={onRefresh}
          disabled={busy}
          aria-label="Refresh"
          data-testid="devotional-health-refresh"
          className="ml-auto text-white/50 hover:text-gold-400 disabled:opacity-40 transition-colors"
        >
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-white/50 text-xs mb-6">
        Today · <span className="text-gold-300 font-mono" data-testid="devotional-health-today">{health?.today || "—"}</span>
        {" · "}
        <span data-testid="devotional-health-cached-count">
          {cachedCount} of {total} languages ready
        </span>
      </p>

      <div className="grid grid-cols-2 gap-2" data-testid="devotional-health-grid">
        {languages.map((l) => (
          <motion.div
            key={l.lang}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
            data-testid={`devotional-health-${l.lang}`}
            className={`flex items-center gap-2 px-3 py-2 border ${
              l.cached
                ? "border-emerald-500/30 bg-emerald-500/[0.06]"
                : "border-red-500/30 bg-red-500/[0.06]"
            }`}
            title={l.title || (l.cached ? "Cached" : "Not yet generated")}
          >
            <span className="text-base leading-none">{LANG_FLAGS[l.lang] || "🏳️"}</span>
            <span className="text-xs text-white/80 flex-1 truncate">{LANG_NAME[l.lang] || l.lang}</span>
            {l.cached ? (
              <CheckCircle2 size={13} className="text-emerald-400" />
            ) : (
              <AlertCircle size={13} className="text-red-400" />
            )}
          </motion.div>
        ))}
      </div>

      <button
        onClick={onPrewarm}
        disabled={prewarming || isFullyCached}
        data-testid="devotional-force-prewarm"
        title={isFullyCached ? "All languages already cached — no action needed" : "Force-generate today's devotional in every language"}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] py-2.5 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {prewarming ? (
          <><Loader2 size={12} className="animate-spin" /> Prewarming…</>
        ) : isFullyCached ? (
          <><CheckCircle2 size={12} /> All 8 languages cached</>
        ) : (
          <>Force prewarm all 8 languages</>
        )}
      </button>
    </div>
  );
}
